import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, MessageCircle, TrendingUp, BarChart, Settings, Users, Target } from 'lucide-react';
import { cjmsApi } from '../api/cjmsApi';

interface GrowthDashboardProps {
    leadId?: string;
    email: string;
}

const GROWTH_TOOLS = [
    { id: 'marketing', label: 'Multi-Channel Marketing', desc: 'Social media, email, content marketing strategies', icon: TrendingUp },
    { id: 'crm', label: 'CRM Setup & Training', desc: 'Setup your CRM and learn best practices', icon: Settings },
    { id: 'team', label: 'Team Integration', desc: 'Hire and onboard team members', icon: Users },
    { id: 'planning', label: 'Advanced Planning', desc: 'Quarterly planning and goal setting', icon: Target },
];

const HEALTH_CHECKS = [
    { id: 'revenue', label: 'Revenue Growth', desc: 'Track revenue trends and targets', icon: BarChart },
    { id: 'churn', label: 'Customer Churn', desc: 'Monitor and reduce churn', icon: ArrowRight },
    { id: 'nps', label: 'NPS Score', desc: 'Customer satisfaction tracking', icon: BarChart },
];

export const GrowthDashboard: React.FC<GrowthDashboardProps> = ({
    leadId = '',
    email = '',
}) => {
    const [tools, setTools] = useState<any>(null);
    const [healthChecks, setHealthChecks] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [step, setStep] = useState<'loading' | 'success' | 'error'>('loading');

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [toolsRes, healthRes] = await Promise.all([
                    cjmsApi.getGrowthTools(email),
                    cjmsApi.getGrowthTools(email), // Use same endpoint for health checks
                ]);

                if (toolsRes.success) {
                    setTools(toolsRes.tools);
                }
                if (healthRes.success) {
                    setHealthChecks(healthRes.tools?.healthChecks || []);
                }

                setStep('success');
            } catch {
                setStep('error');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [email]);

    if (loading) {
        return (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-lg mx-auto p-6 text-center">
                <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
                    <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4" />
                    <h2 className="text-lg font-bold text-slate-800 mb-2">Loading Growth Tools</h2>
                    <p className="text-xs text-slate-500">Preparing your scaling resources...</p>
                </div>
            </motion.div>
        );
    }

    if (step === 'error') {
        return (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-lg mx-auto p-6 text-center">
                <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
                    <p className="text-sm text-red-600 font-medium mb-4">Unable to load growth tools</p>
                    <p className="text-xs text-slate-500 mb-4">Check your status or contact support</p>
                    <button onClick={() => setStep('loading')} className="px-4 py-2 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">
                        Retry
                    </button>
                </div>
            </motion.div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
        >
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
                <div className="flex items-center gap-2">
                    <Target className="w-4 h-4 text-purple-600" />
                    <h3 className="text-sm font-bold text-slate-800">Growth & Scaling</h3>
                </div>
                <span className="text-[10px] font-medium text-purple-600">Ready to scale</span>
            </div>

            <div className="p-4 space-y-4">
                {/* Growth Tools */}
                {tools?.growthStrategies?.length > 0 && (
                    <div>
                        <h4 className="text-xs font-bold text-slate-700 mb-2">Growth Tools</h4>
                        <div className="space-y-2">
                            {tools.growthStrategies.map(tool => {
                                const Icon = tool.icon;
                                return (
                                    <div key={tool.id} className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg border border-purple-100">
                                        <Icon className="w-4 h-4 text-purple-600" />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs font-semibold text-slate-800">{tool.label}</p>
                                            <p className="text-[10px] text-slate-500">{tool.desc}</p>
                                        </div>
                                        <ArrowRight className="w-3 h-3 text-purple-600" />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Health Checks */}
                <div>
                    <h4 className="text-xs font-bold text-slate-700 mb-2">Health Checks</h4>
                    <div className="space-y-2">
                        {healthChecks.map(check => {
                            const Icon = check.icon;
                            return (
                                <div key={check.id} className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-100">
                                    <Icon className="w-4 h-4 text-green-600" />
                                    <p className="text-xs font-semibold text-slate-800">{check.label}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Next Review */}
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                    <div className="flex items-center gap-2 mb-2">
                        <MessageCircle className="w-3 h-3 text-amber-600" />
                        <p className="text-xs font-semibold text-amber-700">Quarterly Review</p>
                    </div>
                    <p className="text-[10px] text-slate-600">Review your growth and adjust strategies every 3 months. Book your review now.</p>
                </div>
            </div>

            {/* Support */}
            <div className="px-4 pb-4">
                <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg border border-slate-100">
                    <MessageCircle className="w-3 h-3 text-slate-400" />
                    <p className="text-[9px] text-slate-600">
                        Need help with growth? WhatsApp +27 81 714 2228 — responded within 6-12 hours.
                    </p>
                </div>
            </div>
        </motion.div>
    );
};
